/**
 * <lk-ugc-grid> Web Component
 *
 * Drives the UGC video grid (sections/custom_lk-ugc-grid.liquid). Videos are
 * import-on-interaction: each card ships an inert <template data-lk-video>
 * holding its <video> (Shopify-hosted) or <iframe> (YouTube/Vimeo) markup, so
 * nothing is fetched until the visitor clicks the play button. On click the
 * template content is cloned over the poster and playback starts.
 *
 * prefers-reduced-motion: the video is still swapped in (that is the user's
 * explicit request), but playback is never started programmatically — the
 * visitor uses the player's own controls instead.
 *
 * Usage (in Liquid):
 *   <lk-ugc-grid>
 *     <li data-lk-card>
 *       <div data-lk-media>poster</div>
 *       <button data-lk-play aria-label="Play video from @handle">…</button>
 *       <template data-lk-video><video …></video></template>
 *     </li>
 *   </lk-ugc-grid>
 */

class LkUgcGrid extends HTMLElement {
  connectedCallback() {
    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.handleClick = this.handleClick.bind(this);
    this.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
  }

  get prefersReducedMotion() {
    return this.reducedMotionQuery.matches;
  }

  handleClick(event) {
    const button = event.target.closest('[data-lk-play]');
    if (!button) return;

    const card = button.closest('[data-lk-card]');
    if (card) this.activate(card, button);
  }

  activate(card, button) {
    const template = card.querySelector('template[data-lk-video]');
    if (!template || card.classList.contains('is-playing')) return;

    // One video at a time: pause any card already playing.
    this.querySelectorAll('.lk-ugc-grid__video video').forEach((video) => video.pause());

    const holder = document.createElement('div');
    holder.className = 'lk-ugc-grid__video';
    holder.appendChild(template.content.cloneNode(true));
    card.appendChild(holder);
    card.classList.add('is-playing');
    button.hidden = true;

    const video = holder.querySelector('video');
    const iframe = holder.querySelector('iframe');

    if (video) {
      video.controls = true;
      video.setAttribute('playsinline', '');
      if (!this.prefersReducedMotion) {
        const played = video.play();
        if (played) played.catch(() => {});
      }
      video.focus();
    } else if (iframe) {
      if (!this.prefersReducedMotion) {
        const joiner = iframe.src.includes('?') ? '&' : '?';
        iframe.src = `${iframe.src}${joiner}autoplay=1`;
      }
      iframe.focus();
    }
  }
}

if (!customElements.get('lk-ugc-grid')) {
  customElements.define('lk-ugc-grid', LkUgcGrid);
}
